import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Code,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheck,
  IconHistory,
  IconRefresh,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeliveryFilters, DeliveryOutcome, DeliveryRecord } from './types';

export interface IntegrationDeliveryHistoryProps {
  deliveries: DeliveryRecord[];
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  filters: DeliveryFilters;
  onFilterChange: (filters: DeliveryFilters) => void;
  onResend: (deliveryId: string) => void;
  /**
   * Optional list of event-type filter options. Apps know best which events
   * exist for the current integration, so they pass them in.
   */
  eventTypeOptions?: { value: string; label: string }[];
}

const OUTCOME_COLORS: Record<DeliveryOutcome, string> = {
  success: 'green',
  retry: 'yellow',
  dlq: 'red',
  replayed: 'blue',
};

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Paginated delivery-attempts table with filters + per-row "View payload"
 * expand and "Resend" action.
 *
 * State (page, filters) is owned by the parent; this component renders + emits.
 */
export function IntegrationDeliveryHistory({
  deliveries,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  filters,
  onFilterChange,
  onResend,
  eventTypeOptions,
}: IntegrationDeliveryHistoryProps) {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  const outcomeOptions = [
    { value: '', label: t('integrations.deliveries.allOutcomes') },
    { value: 'success', label: t('integrations.outcomes.success') },
    { value: 'retry', label: t('integrations.outcomes.retry') },
    { value: 'dlq', label: t('integrations.outcomes.dlq') },
    { value: 'replayed', label: t('integrations.outcomes.replayed') },
  ];

  const columns = [
    {
      accessor: 'expand',
      title: '',
      width: 36,
      render: (row: DeliveryRecord) => (
        <Box c="dimmed" style={{ display: 'flex', alignItems: 'center' }}>
          {expandedIds.includes(row.id) ? (
            <IconChevronDown size={16} />
          ) : (
            <IconChevronRight size={16} />
          )}
        </Box>
      ),
    },
    {
      accessor: 'created_at',
      title: t('integrations.deliveries.when'),
      render: (row: DeliveryRecord) => <Text size="sm">{formatTimestamp(row.created_at)}</Text>,
    },
    {
      accessor: 'event_type',
      title: t('integrations.deliveries.event'),
      render: (row: DeliveryRecord) => (
        <Text size="sm" c="dimmed">
          {/* Engine doesn't include event_type on the delivery row; apps may
              join in metadata client-side. We surface the event_id so users
              can correlate. */}
          {row.event_id.slice(0, 8)}
        </Text>
      ),
    },
    {
      accessor: 'attempt_number',
      title: t('integrations.deliveries.attempt'),
      render: (row: DeliveryRecord) => <Text size="sm">#{row.attempt_number}</Text>,
    },
    {
      accessor: 'outcome',
      title: t('integrations.deliveries.outcome'),
      render: (row: DeliveryRecord) => (
        <Badge color={OUTCOME_COLORS[row.outcome]} variant="light" radius="sm">
          {t(`integrations.outcomes.${row.outcome}`)}
        </Badge>
      ),
    },
    {
      accessor: 'http_status',
      title: t('integrations.deliveries.status'),
      render: (row: DeliveryRecord) =>
        row.http_status ? (
          <Text size="sm">{row.http_status}</Text>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
    },
    {
      accessor: 'latency_ms',
      title: t('integrations.deliveries.latency'),
      render: (row: DeliveryRecord) =>
        row.latency_ms !== undefined ? (
          <Text size="sm">{row.latency_ms} ms</Text>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
    },
    {
      accessor: 'error_class',
      title: t('integrations.deliveries.error'),
      render: (row: DeliveryRecord) =>
        row.error_class ? (
          <Tooltip label={row.error_detail ?? ''} multiline maw={400} disabled={!row.error_detail}>
            <Text size="sm" c="red">
              {row.error_class}
            </Text>
          </Tooltip>
        ) : (
          <Text size="sm" c="dimmed">
            —
          </Text>
        ),
    },
    {
      accessor: 'actions',
      title: t('integrations.list.actions'),
      textAlign: 'right' as const,
      render: (row: DeliveryRecord) => (
        <Tooltip label={t('integrations.actions.resend')}>
          <ActionIcon
            variant="subtle"
            onClick={(e) => {
              e.stopPropagation();
              onResend(row.id);
            }}
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      ),
    },
  ];

  const renderDetails = (row: DeliveryRecord) => {
    const isSuccess = row.outcome === 'success' || row.outcome === 'replayed';
    const hasError = !!row.error_class || !!row.error_detail;
    const hasPayload = row.payload_excerpt !== undefined && row.payload_excerpt !== null;
    return (
      <Box p="md" style={{ background: 'var(--mantine-color-gray-0)' }}>
        <Stack gap="xs">
          <Group gap="xs">
            {isSuccess && !hasError ? (
              <IconCircleCheck size={16} color="var(--mantine-color-green-6)" />
            ) : (
              <IconAlertCircle size={16} />
            )}
            <Text fw={500} size="sm">
              {isSuccess && !hasError
                ? row.http_status !== undefined && row.latency_ms !== undefined
                  ? t('integrations.deliveries.successWithDetails', {
                      status: row.http_status,
                      latency: row.latency_ms,
                    })
                  : t('integrations.deliveries.successTitle')
                : t('integrations.deliveries.detailsTitle')}
            </Text>
          </Group>
          <Group gap="md" wrap="wrap">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {t('integrations.deliveries.eventIdLabel')}
              </Text>
              <Code>{row.event_id}</Code>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {t('integrations.deliveries.deliveryIdLabel')}
              </Text>
              <Code>{row.id}</Code>
            </Stack>
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                {t('integrations.deliveries.timestampLabel')}
              </Text>
              <Text size="sm">{new Date(row.created_at).toLocaleString()}</Text>
            </Stack>
          </Group>
          {row.error_detail && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('integrations.deliveries.errorDetail')}
              </Text>
              <Code block>{row.error_detail}</Code>
            </Stack>
          )}
          {hasPayload && (
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                {t('integrations.deliveries.payload')}
              </Text>
              <Code block>
                {typeof row.payload_excerpt === 'string'
                  ? row.payload_excerpt
                  : JSON.stringify(row.payload_excerpt, null, 2)}
              </Code>
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Stack gap="md">
      <Group wrap="wrap" gap="sm" align="flex-end">
        <Select
          label={t('integrations.deliveries.filterOutcome')}
          data={outcomeOptions}
          value={filters.outcome ?? ''}
          onChange={(v) => onFilterChange({ ...filters, outcome: (v as DeliveryOutcome) || '' })}
          placeholder={t('integrations.deliveries.filterOutcome')}
          clearable
          w={180}
          size="xs"
        />
        {eventTypeOptions && eventTypeOptions.length > 0 && (
          <Select
            label={t('integrations.deliveries.filterEvent')}
            data={[
              { value: '', label: t('integrations.deliveries.allEvents') },
              ...eventTypeOptions,
            ]}
            value={filters.event_type ?? ''}
            onChange={(v) => onFilterChange({ ...filters, event_type: v ?? undefined })}
            placeholder={t('integrations.deliveries.filterEvent')}
            clearable
            w={220}
            size="xs"
          />
        )}
        <TextInput
          type="datetime-local"
          value={filters.from ?? ''}
          onChange={(e) => onFilterChange({ ...filters, from: e.currentTarget.value })}
          placeholder={t('integrations.deliveries.from')}
          label={t('integrations.deliveries.from')}
          size="xs"
        />
        <TextInput
          type="datetime-local"
          value={filters.to ?? ''}
          onChange={(e) => onFilterChange({ ...filters, to: e.currentTarget.value })}
          placeholder={t('integrations.deliveries.to')}
          label={t('integrations.deliveries.to')}
          size="xs"
        />
        <Button
          variant="subtle"
          size="xs"
          onClick={() => onFilterChange({})}
          disabled={!filters.outcome && !filters.event_type && !filters.from && !filters.to}
        >
          {t('integrations.deliveries.clearFilters')}
        </Button>
      </Group>

      <DataTable
        minHeight={deliveries.length === 0 ? 280 : undefined}
        withTableBorder
        borderRadius="md"
        shadow="xs"
        records={deliveries}
        columns={columns}
        totalRecords={totalCount}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={onPageChange}
        fetching={loading}
        noRecordsText=""
        rowExpansion={{
          allowMultiple: true,
          expanded: {
            recordIds: expandedIds,
            onRecordIdsChange: (ids: unknown[]) => setExpandedIds(ids as string[]),
          },
          content: ({ record }) => renderDetails(record),
        }}
        emptyState={
          deliveries.length === 0 ? (
            <Box py="xl" px="md">
              <Stack align="center" gap="xs">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconHistory size={24} />
                </ThemeIcon>
                <Text size="sm" fw={500}>
                  {t('integrations.deliveries.empty')}
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={360}>
                  {t('integrations.deliveries.emptyDescription')}
                </Text>
              </Stack>
            </Box>
          ) : (
            <></>
          )
        }
      />
    </Stack>
  );
}
